import { useState, useEffect } from 'react';
import ProjectSelect from '../shared/ProjectSelect/ProjectSelect';

/**
 * TimesheetForm - Wiederverwendbare Komponente für die Stundenerfassung.
 * 
 * Props:
 * - initialData: { date, tasks, notes } (für Bearbeitungsmodus)
 * - onSubmit: Callback-Funktion (data, isUpdate)
 * - onCancel: Callback-Funktion (für Abbrechen bei Bearbeitung)
 * - loading: Boolean (Ladezustand der übergeordneten Komponente)
 * - error: String (Fehlermeldung)
 * - entryId: ID des Eintrags (null bei neuem Eintrag)
 * - submitButtonColor: String (CSS-Farbe für den Submit-Button)
 */
function TimesheetForm({
    initialData,
    onSubmit,
    onCancel,
    loading,
    error,
    entryId,
    submitButtonColor
}) {
    const [selectedDate, setSelectedDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);
    const [projectId, setProjectId] = useState(initialData?.project_id || null);
    const [tasks, setTasks] = useState(initialData?.tasks || [{ description: '', hours: '' }]);
    const [notes, setNotes] = useState(initialData?.notes || '');

    // Synchronisieren, wenn sich initialData ändert (z.B. beim Laden eines Eintrags zum Bearbeiten)
    useEffect(() => {
        if (initialData) {
            setSelectedDate(initialData.date);
            setProjectId(initialData.project_id || null);
            setTasks(JSON.parse(JSON.stringify(initialData.tasks))); // Deep Copy
            setNotes(initialData.notes || '');
        }
    }, [initialData]);

    const handleTaskChange = (index, field, value) => {
        const newTasks = tasks.map((task, i) =>
            i === index ? { ...task, [field]: value } : task
        );
        setTasks(newTasks);
    };

    const handleHoursBlur = (index, value) => {
        if (!value) return;

        let num = parseFloat(value.toString().replace(',', '.'));
        if (isNaN(num)) return;

        if (num < 0) num = 0;
        const rounded = Math.round(num * 4) / 4;

        const newTasks = tasks.map((task, i) =>
            i === index ? { ...task, hours: rounded.toString() } : task
        );
        setTasks(newTasks);
    };

    const addTaskRow = () => {
        setTasks([...tasks, { description: '', hours: '' }]);
    };

    const removeTaskRow = (index) => {
        const newTasks = tasks.filter((_, i) => i !== index);
        setTasks(newTasks);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            date: selectedDate,
            project_id: projectId,
            tasks,
            notes
        }, !!entryId);
    };

    const totalHours = tasks.reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0);

    return (
        <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
            {error && <div className="error-message" style={{ marginBottom: '15px' }}>{error}</div>}

            <div className="form-group-date">
                <label>Datum</label>
                <input
                    type="date"
                    value={selectedDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    required
                />
            </div>
            
            <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Projekt (optional)</label>
                <ProjectSelect 
                    value={projectId}
                    onChange={(val) => setProjectId(val)}
                />
            </div>

            <div className="tasks-list">
                <label className="tasks-section-label">Tätigkeiten</label>

                {tasks.map((task, index) => (
                    <div key={index} className="task-row">
                        <div className="task-input-desc">
                            <input
                                type="text"
                                placeholder="Was wurde gemacht?"
                                value={task.description}
                                onChange={(e) => handleTaskChange(index, 'description', e.target.value)}
                                required
                            />
                        </div>

                        <div className="task-input-hours">
                            <input
                                type="number"
                                step="0.25"
                                placeholder="Std."
                                min="0"
                                value={task.hours}
                                onChange={(e) => handleTaskChange(index, 'hours', e.target.value)}
                                onBlur={(e) => handleHoursBlur(index, e.target.value)}
                                required
                            />
                        </div>

                        {tasks.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeTaskRow(index)}
                                className="btn-remove"
                                title="Zeile entfernen"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <button type="button" onClick={addTaskRow} className="btn-add-task">
                <span>+</span> Tätigkeit hinzufügen
            </button>

            <div className="form-summary-box">
                <div className="summary-row">
                    <span className="summary-total-label">Gesamtstunden:</span>
                    <span className="summary-total-value">{totalHours} h</span>
                </div>
                <div className="form-group">
                    <label className="tasks-section-label" style={{ marginBottom: '6px' }}>Notiz</label>
                    <textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Optionale Notiz..."
                    />
                </div>
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: '10px' }}>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-submit-form"
                    style={{
                        background: submitButtonColor || (entryId ? '#f59e0b' : '#10b981'),
                        flex: 1,
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    {loading ? 'Speichere...' : (entryId ? 'Änderungen speichern' : 'Bericht senden')}
                </button>

                {entryId && onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn-header-secondary"
                        style={{
                            padding: '0 20px',
                            height: 'auto',
                            flex: 1,
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        Abbrechen
                    </button>
                )}
            </div>
        </form>
    );
}

export default TimesheetForm;
